import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionInfoService } from '../service/connection-info.service';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';

/**
 * Contains the rotation status of an article and the corridor value of the accepting connection on that article.
 * Used for collecting Information about all accepting connections from a TextureArea to the MountingRail.
 */
type CorridorInfo = {
    corridor: number;
    isRotated: boolean;
};

@Injectable({ providedIn: 'root' })
export class CorridorCalculationService {
  static readonly CORRIDOR_LEVEL_FACTOR: number = 100;

  public constructor(
    private connectionInfoService: ConnectionInfoService,
  ) {
  }

  /**
   * Calculate corridor value for given texture area connection.
   *
   * @param connection the connection to calculate the corridor value for
   * @returns the calculated corridor value
   */
  public calculateCorridorValue(connection: Connection): number {
    const corridorInfos: CorridorInfo[] = this.collectTextureCorridorInfos(connection);

    let corridor: number = 0;
    let useRotation: boolean = true;
    for (const corridorInfo of corridorInfos) {
      const corridorValue = corridorInfo.corridor === 0 ? 0.5 : corridorInfo.corridor;
      corridor = (corridor * CorridorCalculationService.CORRIDOR_LEVEL_FACTOR) + corridorValue;
      if (useRotation && corridorInfo.isRotated) {
        corridor *= -1;
      }
      useRotation = false; // Only consider the rotation of the first element (rail article)
    }

    return corridor;
  }

  /**
   * Collects information required to ccalculate the corridor value for the given texture area.
   * @param connection The (Texture)connection to collect information for.
   * @returns an array of CorridorInfo objects, starting from the mounting rail up to the given texture area.
   */
  private collectTextureCorridorInfos(connection: Connection): CorridorInfo[] {
    const corridorInfos: CorridorInfo[] = [];

    if (!connection || !connection.isTextureArea()) {
      return corridorInfos;
    }

    // We only consider the corridor values of the accepting connections on the way to the mounting rail (excluding the mounting rail itself).
    // let currentConnection: Connection | undefined = this.findRelatedAcceptingConnectionOnParent(connection);
    let currentConnection: Connection | undefined = connection;
    while (currentConnection && !ArticleUtils.isMountingRail(currentConnection.article)) {
      // Skip labeling articles (they don't have corridor values)
      if (!ArticleUtils.isLabelingArticle(currentConnection.article)) {
        const corridorValue = currentConnection.metaElementVO.corridor;
        const corridorInfo: CorridorInfo = {
          corridor: corridorValue ? corridorValue : 0, // fall back: null -> 0
          isRotated: ArticleUsageUtils.isArticleRotated(currentConnection.articleContainer.articleUsage),
        };
        corridorInfos.unshift(corridorInfo);
      }

      const parentConnection = this.findParentAcceptingConnection(currentConnection);
      if (!parentConnection) {
        // Error: could not find the related accepting connection on the parent.
        // CorridorInfos are incomplete, return empty.
        Log.warn(`Could not find parent accepting connection for connection ${connection.connectionName}. Collected corridor infos so far:`, JSON.stringify(corridorInfos));
        corridorInfos.length = 0; // empty the result
      }

      currentConnection = parentConnection;
    }

    return corridorInfos;
  }

  /**
   * Using the given connection, find the parent accepting connection to use for corridor calculation.
   * @param connection Find
   * @returns
   */
  private findParentAcceptingConnection(connection: Connection): Connection | undefined {
    if (connection.isTextureArea()) {
      if (ArticleUtils.isDynamicArticle(connection.article)) {
        // Dyamic Labeling article (labeling strip).
        return this.findOpposingAcceptingParentConnection(connection) ??
         this.findRepresentativeAcceptingParentConnection(connection);
      } else {
        return this.findRepresentativeAcceptingParentConnection(connection);
      }
    } else {
      // Regular article
      // Try to find a single backward connection on the article .
      const allGivingUsages: ConnectionAreaUsageVO[] = ArticleUsageUtils.backwardConnectionAreaUsages(connection.articleContainer.articleUsage);
      if (allGivingUsages.length === 0) {
        Log.warn(`Article ${connection.articleContainer.articleGUID} (${connection.articleContainer.article.articleNo}) has no backward connections`);
        return undefined;
      }
      // Found multiple backward connections. Use first for calculating corridor.
      if (allGivingUsages.length > 1) {
        Log.warn(`Article ${connection.articleContainer.articleGUID} (${connection.articleContainer.article.articleNo}) has multiple accepting backward connections. Continuing with first.`, allGivingUsages);
      }
      return this.connectionInfoService.findConnectionByIdentity(allGivingUsages[0].connection.acceptingAreaId);
    }
  }

  /**
   * Find the accepting connection directly opposed to the given dynamic texture connection.
   * @param dynamicTextureConnection The dynamic texture connection to find the opposing accepting parent connection for.
   * @returns The opposing accepting parent connection or undefined if none could be found.
   */
  private findOpposingAcceptingParentConnection(dynamicTextureConnection: Connection): Connection | undefined {
    const textureConnectionName = dynamicTextureConnection.metaElementUsage?.identity?.metaElementName;
    if (!textureConnectionName || !textureConnectionName.startsWith(MetaElementVO.TEXTURE_PREFIX)) {
      Log.warn(`Unexpected metaElementName ${textureConnectionName} on connection ${dynamicTextureConnection.connectionName}. Expected to start with ${MetaElementVO.TEXTURE_PREFIX}`);
      return undefined;
    }

    // Derive the name of the related giving connection from the textureArea name (e.g. ta1 -> cn1).
    const givingConnectionName = textureConnectionName
      .replace(MetaElementVO.TEXTURE_PREFIX, MetaElementVO.CONNECTION_PREFIX);

    // Find the giving connection
    // Note: don't replace this with connectionInfoService.findConnectionByIdentity()
    //       as that does not work on "temporary" labeling-articles
    //       (temporary articles can't be found in global and the method returns null).
    const givingConnection: Connection | null = dynamicTextureConnection.articleContainer.findConnectionByName(givingConnectionName);
    if (!givingConnection) {
      // connection has no opposing parent connection (no adapter)
      return undefined;
    }

    // Find the accepting connection on the parent article
    return this.findAcceptingConnectionOnParentFor(givingConnection);
  }

  /**
   * Finds a representative accepting connection on the parent connection.articleContainer is mounted on.
   * @param connection The connection to find the parent accepting connection for.
   * @returns The representative accepting parent connection or undefined if none could be found.
   */
  private findRepresentativeAcceptingParentConnection(connection: Connection): Connection | undefined {
    // Find all parent accepting connections connection.articleContainer is connected to.
    const allParentAcceptingConnections: Connection[] = connection.articleContainer.connections
      .filter(conn => this.isGivingConnection(conn))
      .map(conn => this.findAcceptingConnectionOnParentFor(conn))
      .filter((conn): conn is Connection => conn !== undefined);

    // Sanity check: all accepting connections should have the same corridor value
    // (This is a simplification, the proper check would be to collect the
    //  complete CorridorInfo array for each parent connection and compare those)
    const allEqual = allParentAcceptingConnections
      .map((conn) => conn.metaElementVO.corridor)
      .every((val, _, arr) => val === arr[0]);
    if (!allEqual) {
      // Problem: inconsistent corridor values on parent connections
      Log.warn(`Inconsistent corridor values on parent accepting connections for connection ${connection.connectionName}`);
      return undefined;
    }

    // Return the first parent connection (all are equivalent regarding corridor value)
    return allParentAcceptingConnections[0];
  }

  private isGivingConnection(connection: Connection): boolean {
    if (connection.metaElementVO instanceof ConnectionAreaVO) {
      return connection.metaElementVO.connectionSide === ConnectionAreaVO.SIDE_GIVING;
    }
    return false;
  }

  private findAcceptingConnectionOnParentFor(givingConnection: Connection): Connection | undefined {
    if (!this.isGivingConnection(givingConnection)) {
      return undefined;
    }
    const givingUsage: ConnectionAreaUsageVO = <ConnectionAreaUsageVO>givingConnection.metaElementUsage;
    const result = this.connectionInfoService.findConnectionByIdentity(givingUsage.connection.acceptingAreaId);
    return result;
  }
}
